import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import AdminPanel from "@/pages/AdminPanel";
import CanteenManagement from "@/pages/CanteenManagement";
import AppSelector from "@/pages/AppSelector";
import InventoryHome from "@/pages/InventoryHome";
import TrainingHome from "@/pages/training/TrainingHome";
import TrainingDetail from "@/pages/training/TrainingDetail";
import TakeAssessment from "@/pages/training/TakeAssessment";
import AssessmentResult from "@/pages/training/AssessmentResult";
import CertificateView from "@/pages/training/CertificateView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  const value = {
    user,
    setUser,
    loading,
    logout,
    checkAuth,
    isAdmin: !!user,
    isSuperAdmin: user?.is_super_admin,
    isTrainingAdmin: user?.is_super_admin || user?.training_role === "training_admin",
    isDepartmentHead: user?.training_role === "department_head",
    // Per-user, per-module access (server-computed)
    canAccessMap:      !!user && (user.effective_access?.map?.view ?? false),
    canEditMap:        !!user && (user.effective_access?.map?.edit ?? false),
    canAccessCanteen:  !!user && (user.effective_access?.canteen?.view ?? false),
    canEditCanteen:    !!user && (user.effective_access?.canteen?.edit ?? false),
    canAccessTraining: !!user && (user.effective_access?.training?.view ?? true),
    canEditTraining:   !!user && (user.effective_access?.training?.edit ?? false),
    canAccessAdmin:    !!user && (user.effective_access?.admin?.view ?? false),
    canEditAdmin:      !!user && (user.effective_access?.admin?.edit ?? false),
    canAccessInventory: !!user && (user.effective_access?.inventory?.view ?? false),
    canEditInventory:   !!user && (user.effective_access?.inventory?.edit ?? false),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Auth Callback Component
function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          setUser(response.data);
          // Clear hash and navigate to app selector (home), so user can choose between Operating Map and Canteen
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/', { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Auth callback error:", error);
          const errorMessage = error.response?.data?.detail || "Authentication failed";
          navigate('/login', { replace: true, state: { error: errorMessage } });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}

// Protected Route
function ProtectedRoute({ children, requires }) {
  const { user, loading, canAccessMap, canAccessCanteen, canAccessAdmin, canAccessInventory } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If user data was passed from AuthCallback, use it
  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Module-level access guard
  const allowed = {
    map: canAccessMap,
    canteen: canAccessCanteen,
    admin: canAccessAdmin,
    inventory: canAccessInventory,
  };
  if (requires && allowed[requires] === false) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Admin Route
function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// App Router - Check for session_id synchronously before rendering routes
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppSelector />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requires="map">
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/canteen"
        element={
          <ProtectedRoute requires="canteen">
            <CanteenManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute requires="inventory">
            <InventoryHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <TrainingHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:trainingId"
        element={
          <ProtectedRoute>
            <TrainingDetailRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:trainingId/take"
        element={
          <ProtectedRoute>
            <TakeAssessment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/result/:attemptId"
        element={
          <ProtectedRoute>
            <AssessmentResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/certificate/:certificateId"
        element={
          <ProtectedRoute>
            <CertificateView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

// Employees should never see the admin TrainingDetail page — they go straight to the assessment.
function TrainingDetailRouter() {
  const { isTrainingAdmin } = useAuth();
  const { trainingId } = useParams();
  if (!isTrainingAdmin) {
    return <Navigate to={`/training/${trainingId}/take`} replace />;
  }
  return <TrainingDetail />;
}

export default App;
export { API, BACKEND_URL };
